import React from 'react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import { Button, Checkbox } from '@innovationnorway/component-library';
import { useTranslation } from 'react-i18next';
import useSivaAdvisorOptions from 'src/features/siva/ideas-and-companies/hooks/use-siva-advisor-options';
import { Autocomplete } from 'src/components/Autocomplete/Autocomplete';
import useAddAdvisor from '../../hooks/use-add-advisor';
import { toast } from 'react-toastify';
import ToastContent from '../../../../../components/ToastContent/ToastContent';
import { OneWayInOpportunitySimpleV2Dto } from 'src/api/v2';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from 'src/navigation';
import { useUser } from 'src/features/authorization';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import useOneWayIn from '../../../hooks/use-one-way-in';
import { useCluster } from 'src/features/cluster';
import { parseDateString } from 'src/utils/date';

type Props = {
  show: boolean;
  opportunity: Pick<OneWayInOpportunitySimpleV2Dto, 'id' | 'oneWayInId' | 'assignedTo' | 'clusterId'>;
  onClose: () => void;
};

const AddAdvisorModal: React.FC<Props> = ({ show, opportunity, onClose }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tPageTitles } = useTranslation('pageTitles');
  const [selectedAdvisor, setSelectedAdvisor] = React.useState<string | null>(opportunity.assignedTo ?? null);
  const { advisorOptions } = useSivaAdvisorOptions();
  const { addAdvisorMutation } = useAddAdvisor(opportunity.oneWayInId);
  const [sendEmailToAdvisor, setSendEmailToAdvisor] = React.useState<boolean>(false);
  const { user } = useUser();
  const siteimprove = useSiteimprove();
  const location = useLocation();
  const { cluster } = useCluster();
  const { createOpportunity, oneWayInGroupedFormWOEnrollmentsQuery } = useOneWayIn(opportunity.oneWayInId);

  React.useEffect(() => {
    if (show) {
      setSelectedAdvisor(opportunity.assignedTo ?? null);
    }
  }, [show, opportunity.assignedTo]);

  const handleAddAdvisor = async () => {
    let opportunityId = opportunity.id;

    // Create opportunity if it does not exist
    if (!opportunity.clusterId && !!cluster && !!oneWayInGroupedFormWOEnrollmentsQuery.data) {
      const form = oneWayInGroupedFormWOEnrollmentsQuery.data;
      const createDto = {
        clusterId: cluster.id,
        oneWayInId: form.id!,
        title: form.tittel ?? '',
        municipality: form.prosjektkommune ?? '',
        customerName: form.kontakt?.navn,
        organizationName: form.organisasjon?.navn,
        organizationNumber: form.organisasjon?.orgNr,
        submittedOn: form.innsendtDato ? parseDateString(form.innsendtDato) : undefined,
      };
      const created = await createOpportunity.mutateAsync(createDto);
      if (created) {
        opportunityId = created.id;
      }
    }

    await addAdvisorMutation
      .mutateAsync({
        opportunityId: opportunityId,
        clusterUserId: selectedAdvisor!,
        emailDto: sendEmailToAdvisor
          ? {
              handlersName: user?.displayName ?? '',
            }
          : undefined,
      })
      .then(() => {
        if (location.pathname === ROUTES.ONE_WAY_IN.BASE) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.ASSIGN_ADVISOR,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.SUCCESS,
          );
        }
        toast(
          <ToastContent
            title={tOneWayIn('opportunity.addAdvisor.toastSuccess.title')}
            text={tOneWayIn('opportunity.addAdvisor.toastSuccess.info')}
            success={true}
          />,
          {
            toastId: 'AdvisorAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        );
        if (sendEmailToAdvisor) {
          toast(
            <ToastContent
              title={tOneWayIn('opportunity.addAdvisor.email.toastSuccess.title')}
              text={tOneWayIn('opportunity.addAdvisor.email.toastSuccess.info')}
              success={true}
            />,
            {
              toastId: 'AdvisorAddedEmail',
              autoClose: false,
              position: 'bottom-center',
            },
          );
        }
      })
      .catch(() => {
        if (location.pathname === ROUTES.ONE_WAY_IN.BASE) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.ASSIGN_ADVISOR,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.ERROR,
          );
        }
        toast(
          <ToastContent
            title={tOneWayIn('opportunity.addAdvisor.toastError.title')}
            text={tOneWayIn('opportunity.addAdvisor.toastError.info')}
            success={false}
          />,
          {
            toastId: 'AdvisorNotAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        );
      })
      .finally(() => {
        setSelectedAdvisor(null);
        onClose();
      });
  };

  return (
    <DialogContainer
      title={tOneWayIn('opportunity.addAdvisor.modal.title')}
      show={show}
      onClose={() => {
        onClose();
        setSendEmailToAdvisor(false);
      }}
      className="overflow--visible"
    >
      <p>{tOneWayIn('opportunity.addAdvisor.modal.infoText')}</p>

      <p>
        {tOneWayIn('opportunity.addAdvisor.modal.invite')}{' '}
        <Link to={ROUTES.ACTOR_PROFILE.SETTINGS.ACCESS_CONTROL}>{tPageTitles('accessControls')}</Link>
      </p>
      <Autocomplete
        label={tCommon('advisor')}
        name={'advisor'}
        items={advisorOptions}
        filter={(item, value: string): boolean => {
          const lowerCaseValue = value.toLowerCase();

          return item.text.toLowerCase().includes(lowerCaseValue) || false;
        }}
        onItemSelected={(advisor) => setSelectedAdvisor(advisor.value)}
        itemLabel={(advisor) => `${advisor.text}`}
        renderItem={(advisor) => `${advisor.text}`}
        defaultValue={
          advisorOptions.find((x) => x.value === selectedAdvisor)?.text ||
          tOneWayIn('opportunity.addAdvisor.modal.searchPlaceholder')
        }
        className="add-advisor-modal__autocomplete"
      />
      {selectedAdvisor && selectedAdvisor !== user?.clusterUserId && (
        <Checkbox
          className="margin--0"
          name="sendEmailToAdvisor"
          options={[
            {
              selected: false,
              text: tOneWayIn('opportunity.addAdvisor.sendEmail'),
              value: 'yes',
            },
          ]}
          onChange={(e) => setSendEmailToAdvisor(e.currentTarget.checked)}
        />
      )}
      <div className="display--flex gap--2">
        <Button
          isLoading={addAdvisorMutation.isLoading}
          onClick={handleAddAdvisor}
          disabled={addAdvisorMutation.isLoading}
        >
          {tOneWayIn('opportunity.addAdvisor.addButton')}
        </Button>
        <Button variant="outlined" theme="neutral" onClick={onClose}>
          {tCommon('cancel')}
        </Button>
      </div>
    </DialogContainer>
  );
};

export default AddAdvisorModal;
